import { NetworkStatus } from '@apollo/client';
import Decimal from 'decimal.js';
import { groupBy, isEmpty } from 'lodash';

import {
  AI_ONBOARDING_MODAL_REVIEW_ALL_PAGE_SIZE,
  AI_ONBOARDING_MODAL_SUGGESTION_FORM_KEY_MAP,
} from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.constants';
import { AIOnboardingModalSuggestionKinds } from '@/modules/aiOnboarding/AIOnboardingModal/AIOnboardingModal.types';
import { AiSuggestionForReviewCard } from '@/modules/aiSuggestions/aiSuggestions.contracts';
import {
  BUSINESS_ENTITY_KINDS,
  NON_TRUST_CHARITABLE_ENTITY_KINDS,
  PERSONAL_ACCOUNT_ENTITY_KINDS,
  TRUST_ENTITY_KINDS,
} from '@/modules/entities/entities.constants';
import {
  AiSuggestionKind,
  AiSuggestionWhereInput,
  ClientProfileRelationshipType,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';

export function isAIOnboardingModalPageLoading(
  hasCurrentData: boolean,
  hasPreviousData: boolean,
  networkStatus: NetworkStatus
) {
  if (networkStatus === NetworkStatus.setVariables) {
    return true;
  }

  return !(
    hasCurrentData ||
    (hasPreviousData && networkStatus === NetworkStatus.refetch)
  );
}

export function getAIOnboardingModalSuggestionQueryOptions({
  initialSelectedSuggestion,
  suggestionsWhere,
}: {
  initialSelectedSuggestion: AiSuggestionForReviewCard | undefined;
  suggestionsWhere: AiSuggestionWhereInput;
}) {
  if (initialSelectedSuggestion) {
    return {
      first: 1,
      suggestionsWhere: { id: initialSelectedSuggestion.id },
    };
  }

  return {
    first: AI_ONBOARDING_MODAL_REVIEW_ALL_PAGE_SIZE,
    suggestionsWhere,
  };
}

/**
 * Groups suggestions by the form path they correspond to.
 * Holdings suggestions (assets, liabilities, life insurance) are sorted by descending monetary value.
 *
 * @param suggestions
 */
export function groupSuggestionsByFormPath<T extends AiSuggestionForReviewCard>(
  suggestions: T[],
  grantorIds?: string[]
): {
  individuals: T[];
  organizations: T[];
  entities: T[];
  testamentaryEntities: T[];
  assets: T[];
  liabilities: T[];
  lifeInsurancePolicies: T[];
} {
  if (isEmpty(suggestions)) {
    return {
      individuals: [],
      organizations: [],
      entities: [],
      testamentaryEntities: [],
      assets: [],
      liabilities: [],
      lifeInsurancePolicies: [],
    };
  }

  const filtered = suggestions.filter(
    (s) => s.kind in AI_ONBOARDING_MODAL_SUGGESTION_FORM_KEY_MAP
  );

  const grouped = groupBy(filtered, (s) => {
    return AI_ONBOARDING_MODAL_SUGGESTION_FORM_KEY_MAP[
      s.kind as AIOnboardingModalSuggestionKinds
    ];
  });

  // Sort assets by descending assetValue
  const sortedAssets = (grouped.assets || []).sort((a, b) =>
    new Decimal(b.asset?.assetValue || '0').comparedTo(
      new Decimal(a.asset?.assetValue || '0')
    )
  );

  // Sort liabilities by descending outstandingBalance
  const sortedLiabilities = (grouped.liabilities || []).sort((a, b) =>
    new Decimal(b.liability?.outstandingBalance || '0').comparedTo(
      new Decimal(a.liability?.outstandingBalance || '0')
    )
  );

  // Sort life insurance by descending deathBenefitAmount
  const sortedLifeInsurance = (grouped.lifeInsurancePolicies || []).sort(
    (a, b) =>
      new Decimal(b.lifeInsurance?.deathBenefitAmount || '0').comparedTo(
        new Decimal(a.lifeInsurance?.deathBenefitAmount || '0')
      )
  );

  // Sort individuals by grantor relationship proximity
  const sortedIndividuals = [...(grouped.individuals || [])];
  if (grantorIds && grantorIds.length > 0) {
    sortedIndividuals.sort((a, b) => {
      const proxA = getRelationshipProximityScore(a, grantorIds);
      const proxB = getRelationshipProximityScore(b, grantorIds);
      return proxA - proxB;
    });
  }

  return {
    individuals: sortedIndividuals,
    organizations: grouped.organizations || [],
    entities: grouped.entities || [],
    testamentaryEntities: grouped.testamentaryEntities || [],
    assets: sortedAssets,
    liabilities: sortedLiabilities,
    lifeInsurancePolicies: sortedLifeInsurance,
  };
}

/**
 * Relationship types considered "children" of a grantor.
 */
const CHILD_RELATIONSHIP_TYPES: ClientProfileRelationshipType[] = [
  ClientProfileRelationshipType.Child,
  ClientProfileRelationshipType.StepChild,
];

/**
 * Relationship types considered "spouse or domestic partner" of a grantor.
 */
const SPOUSE_RELATIONSHIP_TYPES: ClientProfileRelationshipType[] = [
  ClientProfileRelationshipType.Spouse,
  ClientProfileRelationshipType.DomesticPartner,
];

/**
 * Relationship types considered "grandchild" of a grantor.
 */
const GRANDCHILD_RELATIONSHIP_TYPES: ClientProfileRelationshipType[] = [
  ClientProfileRelationshipType.Grandchild,
];

/**
 * Returns a numeric score representing how close a suggested individual is to
 * the household's grantors. Lower scores indicate closer relationships.
 *
 * Scoring:
 *   0 = isSuggestedAsGrantor (person IS a grantor)
 *   1 = Child or StepChild of a grantor
 *   2 = Spouse or DomesticPartner of a grantor
 *   3 = Grandchild of a grantor
 *   4 = Any other relationship to a grantor
 *   5 = No relationship to any grantor
 *
 * When a person has multiple relationships to grantors, the closest (minimum)
 * score is used.
 */
export function getRelationshipProximityScore(
  suggestion: AiSuggestionForReviewCard,
  grantorIds: string[]
): number {
  // Non-individual suggestions don't get proximity scoring
  if (suggestion.kind !== AiSuggestionKind.OnboardingIndividual) {
    return Infinity;
  }

  // Suggested grantors sort first
  if (suggestion.onboardingIndividual?.isSuggestedAsGrantor) {
    return 0;
  }

  const relationships =
    suggestion.onboardingIndividual?.personalRelationships ?? [];

  // Only consider relationships to grantors
  const grantorRelationships = relationships.filter(
    (rel) => rel.clientProfileID && grantorIds.includes(rel.clientProfileID)
  );

  if (grantorRelationships.length === 0) {
    return 5;
  }

  // Find the closest relationship across all grantors
  let minScore = 5;
  for (const rel of grantorRelationships) {
    if (!rel.relationshipType) continue;

    if (CHILD_RELATIONSHIP_TYPES.includes(rel.relationshipType)) {
      minScore = Math.min(minScore, 1);
    } else if (SPOUSE_RELATIONSHIP_TYPES.includes(rel.relationshipType)) {
      minScore = Math.min(minScore, 2);
    } else if (GRANDCHILD_RELATIONSHIP_TYPES.includes(rel.relationshipType)) {
      minScore = Math.min(minScore, 3);
    } else {
      minScore = Math.min(minScore, 4);
    }

    // Short-circuit if we've found the closest possible non-grantor score
    if (minScore === 1) break;
  }

  return minScore;
}

/**
 * Given a list of suggestions and a list of document IDs, returns the ordered
 * list of suggestions by the following criteria:
 *
 * 1. Entity kind bucket (trust, business, non-trust charitable, personal account)
 * 2. Grantor relationship proximity (for individuals, when grantorIds are provided)
 * 3. Document order
 * 4. Node name
 *
 * @param suggestions
 * @param orderedDocumentIds
 * @param grantorIds Optional array of grantor client profile IDs for proximity sorting
 */
export function orderSuggestions<T extends AiSuggestionForReviewCard>(
  suggestions: T[],
  orderedDocumentIds: string[],
  grantorIds?: string[]
): T[] {
  const docIdOrderMap = new Map<string, number>();
  orderedDocumentIds.forEach((id, idx) => docIdOrderMap.set(id, idx));

  function getEntityKindOrder(suggestion: AiSuggestionForReviewCard): number {
    if (suggestion.kind === AiSuggestionKind.OnboardingIndividual) return 0;
    if (suggestion.kind === AiSuggestionKind.OnboardingOrganization) return 0.5;

    const kind = suggestion.onboardingEntity?.entityKind;

    // Non-entity suggestions won't have a kind. We'll put them last in the list,
    // but they will still be ordered by document order.
    if (!kind) return Infinity;

    if (TRUST_ENTITY_KINDS.includes(kind)) return 1;
    if (BUSINESS_ENTITY_KINDS.includes(kind)) return 2;
    if (NON_TRUST_CHARITABLE_ENTITY_KINDS.includes(kind)) return 3;
    if (PERSONAL_ACCOUNT_ENTITY_KINDS.includes(kind)) return 4;

    diagnostics.warn(`[getEntityKindOrder]: unhandled entity kind: ${kind}`);
    return Infinity;
  }

  return suggestions.sort((a, b) => {
    // First, order by entity kind bucket
    const bucketA = getEntityKindOrder(a);
    const bucketB = getEntityKindOrder(b);
    if (bucketA !== bucketB) {
      return bucketA - bucketB;
    }

    // Within the same bucket, order individuals by grantor relationship proximity
    if (grantorIds && grantorIds.length > 0) {
      const proxA = getRelationshipProximityScore(a, grantorIds);
      const proxB = getRelationshipProximityScore(b, grantorIds);
      if (proxA !== proxB) {
        return proxA - proxB;
      }
    }

    // If buckets are the same, order by document order
    const docA = getNodes(a.evidence?.[0]?.fileParagraphs)[0]?.file?.pdfDocument
      ?.id;
    const docB = getNodes(b.evidence?.[0]?.fileParagraphs)[0]?.file?.pdfDocument
      ?.id;

    const docAIndex = docIdOrderMap.get(docA ?? '') ?? Infinity;
    const docBIndex = docIdOrderMap.get(docB ?? '') ?? Infinity;
    if (docAIndex !== docBIndex) {
      return docAIndex - docBIndex;
    }

    return 0;
  });
}

export function getAIOnboardingModalHeader(
  parts: { count: number; nounSingular: string; nounPlural: string }[]
): string {
  const totalCount = parts.reduce((acc, part) => {
    return acc + part.count;
  }, 0);

  if (totalCount === 0) {
    // Return empty string to avoid showing "0 entities identified"
    return '';
  }

  const things = parts
    .filter((part) => part.count > 0)
    .map((part) => {
      return `${part.count} ${part.count > 1 ? part.nounPlural : part.nounSingular}`;
    })
    .join(' & ');

  return `${things} identified`;
}
